"use client";

import { useEffect, useRef } from "react";
import { Button } from "@workspace/ui/components/button";
import WhyWeAsk from "./why-we-ask";
import DoctorTestimonial from "./doctor-testimonial";

interface QuizQuestionOption {
  id: number;
  option: string;
  next_question_id?: string;
}

interface QuizQuestionData {
  id: string;
  question: string;
  subTitle?: string;
  multi_select: boolean;
  options: QuizQuestionOption[];
  whyWeAsk?: { title: string; description: string };
  testimonial?: {
    name: string;
    image: string;
    quote: string;
    designation: string;
  };
}

interface QuizQuestionProps {
  isFreshStart: boolean;
  currentQuestion: QuizQuestionData;
  selectedOptions: number[];
  hasVisitedBefore: boolean;
  onOptionSelect: (optionId: number) => void;
  onNext: () => void;
}

function normalizeOptionLabel(label: string) {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function QuizQuestion({
  isFreshStart = true,
  currentQuestion,
  selectedOptions,
  hasVisitedBefore,
  onOptionSelect,
  onNext,
}: QuizQuestionProps) {
  const autoNavTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isNavigatingRef = useRef(false);
  const onNextRef = useRef(onNext);
  onNextRef.current = onNext;

  useEffect(() => {
    isNavigatingRef.current = false;
    return () => {
      if (autoNavTimeoutRef.current) {
        clearTimeout(autoNavTimeoutRef.current);
        autoNavTimeoutRef.current = null;
      }
    };
  }, [currentQuestion.id]);

  const handleOptionSelect = (optionId: number) => {
    if (isNavigatingRef.current) return;

    onOptionSelect(optionId);

    if (!currentQuestion.multi_select && !hasVisitedBefore) {
      if (autoNavTimeoutRef.current) clearTimeout(autoNavTimeoutRef.current);
      isNavigatingRef.current = true;
      autoNavTimeoutRef.current = setTimeout(() => {
        onNextRef.current();
        autoNavTimeoutRef.current = null;
      }, 300);
    }
  };

  const showDisclaimer = isFreshStart;

  const showNext = currentQuestion.multi_select
    ? selectedOptions.length > 0
    : hasVisitedBefore && selectedOptions.length > 0;

  const showFooter = showDisclaimer || showNext;

  return (
    <>
      <div
        className="flex flex-col flex-1 gap-6 justify-between min-h-0"
        data-testid="question-screen"
        data-question-id={currentQuestion.id}
      >
        <div className="flex-1 flex flex-col gap-6 overflow-y-auto pb-12">
          <div className="flex flex-col gap-3" data-testid={`question-id-${currentQuestion.id}`}>
            <h1
              className="text-primary title-24 lg:title-32"
              data-testid="question-title"
            >
              {currentQuestion.question}
            </h1>
            {currentQuestion.subTitle && (
              <p
                className="text-muted-foreground title-14 font-normal"
                data-testid="question-subtitle"
              >
                {currentQuestion.subTitle}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-3" data-testid="question-options">
            {currentQuestion.options.map((option) => (
              <button
                key={option.id}
                onClick={() => handleOptionSelect(option.id)}
                data-testid={`question-option-${normalizeOptionLabel(option.option)}`}
                className={`w-full py-4 px-4 hover:cursor-pointer flex items-center justify-between gap-[14px] rounded-[20px] border transition-all duration-300 ${
                  selectedOptions.includes(option.id)
                    ? "border-brand bg-white"
                    : "border-[#EBEBEB] bg-white hover:border-brand"
                }`}
              >
                <span
                  className="text-primary text-left title-16 font-normal"
                  data-testid={`question-option-label-${normalizeOptionLabel(option.option)}`}
                >
                  {option.option}
                </span>
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                  {selectedOptions.includes(option.id) ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <rect x="2" y="2" width="20" height="20" rx={currentQuestion.multi_select ? 4 : 10} fill="#000000" />
                      <path d="M15.0527 9.03906C15.3085 8.75697 15.7315 8.75696 15.9873 9.03906C16.2342 9.31141 16.2342 9.74714 15.9873 10.0195L11.5068 14.9609C11.2511 15.2427 10.829 15.2427 10.5732 14.9609L8.0127 12.1367C7.76613 11.8644 7.76607 11.4295 8.0127 11.1572C8.2685 10.8751 8.69146 10.8751 8.94727 11.1572L11.0391 13.4648L15.0527 9.03906Z" fill="white" stroke="white" strokeWidth="0.344444" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="3" width="18" height="18" rx={currentQuestion.multi_select ? 3 : 9} stroke="#E5E5E5" strokeWidth="2" />
                    </svg>
                  )}
                </div>
              </button>
            ))}
          </div>

          {currentQuestion.whyWeAsk && (
            <WhyWeAsk
              title={currentQuestion.whyWeAsk.title}
              description={currentQuestion.whyWeAsk.description}
            />
          )}
        </div>
      </div>

      {currentQuestion.testimonial && (
        <DoctorTestimonial
          title={currentQuestion.testimonial.quote}
          image={currentQuestion.testimonial.image}
          name={currentQuestion.testimonial.name}
          designation={currentQuestion.testimonial.designation}
          layoutClass={!showNext ? "mb-7" : ""}
        />
      )}
  
      {showFooter && (
        <div className="sticky bottom-0 flex flex-col gap-3 pb-5">
          {showDisclaimer && <p className="text-muted-foreground body-12">
              By starting this quiz, you agree that we may use your responses to
              personalize your experience and improve our services. Your data is
              handled securely per our <a href="/privacy-policy" className="underline underline-offset-3">Privacy Policy</a>.
          </p>}
          {showNext && <Button onClick={onNext} variant="default" size="default" className="w-full">Next</Button>}
        </div>
      )}
    </>
  );
}
